import { cn } from '@heidi/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// ============================================================================
// Button Variants
// ============================================================================

export const userAccountMenuButtonVariants = cva(
  // Radix dropdown state - must stay CSS-driven
  'data-[state=open]:bg-background-secondary-pressed',
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        // Override lg size's !p-0 in icon mode to center the avatar (32px button - 24px avatar = 8px / 2 = 4px padding)
        icon: '!p-1',
        'label-icons': cn(
          // Reset sidebar's label-icons grid layout (overrides !grid-rows-[44px_auto] !min-h-16)
          '!min-h-0 !grid-rows-none',
          // 44x44 button with 32x32 avatar centered (matches icon area of other nav items)
          // rounded-lg matches other nav items' icon background (before:rounded-md but we need direct since before:hidden)
          'mx-auto !grid !size-11 !place-items-center !rounded-lg !p-0 before:hidden',
          // Restore background states (normally on ::before, but we hide it)
          'hover:!bg-background-secondary-hover',
          'focus-visible:!bg-background-secondary-hover',
          'active:!bg-background-secondary-pressed',
          'data-[state=open]:!bg-background-secondary-pressed',
          'data-[state=open]:hover:!bg-background-secondary-hover'
        ),
      },
      mobile: {
        // Mobile - larger gap between avatar and text
        true: 'gap-3',
        false: '',
      },
    },
    defaultVariants: {
      collapsible: 'none',
      mobile: false,
    },
  }
);

// ============================================================================
// Avatar
// ============================================================================

// Size, fallback colour and text are owned by the Avatar's `size` / `color`
// props; this only carries the collapse transition.
export const userAccountMenuAvatarClasses =
  'transition-transform duration-200 ease-out motion-reduce:transition-none';

// ============================================================================
// User Info Variants
// ============================================================================

export const userAccountMenuUserInfoVariants = cva(
  'grid flex-1 text-start leading-tight',
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        // Hide user info text when sidebar is collapsed to icon or label-icons mode
        icon: 'hidden',
        'label-icons': 'hidden',
      },
      mobile: {
        // Mobile - larger text to match Button lg sizing
        true: cn(
          'gap-0.5',
          '[&>*:first-child]:text-sm', // Name
          '[&>*:last-child]:text-xs' // Email
        ),
        false: '',
      },
    },
    defaultVariants: {
      collapsible: 'none',
      mobile: false,
    },
  }
);

export const userAccountMenuNameVariants = cva('truncate text-text-primary', {
  variants: {
    mobile: {
      // Mobile - larger text for name
      true: 'text-sm',
      false: '',
    },
  },
  defaultVariants: {
    mobile: false,
  },
});

export const userAccountMenuEmailVariants = cva('truncate text-text-secondary', {
  variants: {
    mobile: {
      // Mobile - larger text for email
      true: 'text-xs',
      false: '',
    },
  },
  defaultVariants: {
    mobile: false,
  },
});

// ============================================================================
// Static Classes (no variants needed)
// ============================================================================

export const userAccountMenuContentClasses = cn(
  'w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg'
);

// ============================================================================
// Type Exports
// ============================================================================

export type UserAccountMenuButtonVariants = VariantProps<
  typeof userAccountMenuButtonVariants
>;
export type UserAccountMenuUserInfoVariants = VariantProps<
  typeof userAccountMenuUserInfoVariants
>;
export type UserAccountMenuNameVariants = VariantProps<
  typeof userAccountMenuNameVariants
>;
export type UserAccountMenuEmailVariants = VariantProps<
  typeof userAccountMenuEmailVariants
>;
